import { ICalendarEvent } from '@app/models/CalendarEvent'
import { CalendarEventType } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import classNames from 'classnames'
import { List } from 'immutable'
import moment from 'moment'
import * as React from 'react'
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { RouteComponentProps } from 'react-router-dom'
import './Calendar.scss'

const localizer = momentLocalizer(moment)

const DEFAULT_VIEW = 'month'

export interface IEventPropGetterResult {
  className?: string
  style?: React.CSSProperties
}

export interface ICalendarProps {
  containerClassName?: string
  events: List<ICalendarEvent>
  onDateRangeChanged?: (start: number, end: number) => void
}

export interface ICalendarState {
  currentDate: Date
  currentView: string
}

type CalendarProps = ICalendarProps & RouteComponentProps<{}>

export class Calendar extends React.PureComponent<
  CalendarProps,
  ICalendarState
> {
  constructor(props: CalendarProps) {
    super(props)
    this.state = {
      currentDate: new Date(),
      currentView: DEFAULT_VIEW,
    }
    bindFunctions(this)
  }

  public componentDidMount() {
    this._handleDateRangeChange()
  }

  public render() {
    return (
      <div
        className={classNames(
          'calendar-container',
          this.props.containerClassName,
        )}
      >
        <div className='calendar-wrapper'>
          <BigCalendar
            localizer={localizer}
            defaultDate={new Date()}
            defaultView={DEFAULT_VIEW}
            eventPropGetter={this._eventPropGetter}
            events={this.props.events.toJS()}
            onNavigate={this._onNavigate}
            onSelectEvent={this._onSelectEvent}
            onView={this._onView}
            views={['month', 'week', 'day']}
          />
        </div>
      </div>
    )
  }

  private _eventPropGetter(event: object) {
    const calEvent = event as ICalendarEvent

    const categories = calEvent.categories.map((c) => {
      return c !== undefined
        ? `event-${c.replace(/\s/g, '-').toLowerCase()}`
        : c
    })

    return {
      className: categories.join(' '),
    }
  }

  private _handleDateRangeChange(
    date: Date = this.state.currentDate,
    view: string = this.state.currentView,
  ) {
    if (this.props.onDateRangeChanged) {
      let start: moment.Moment = moment()
      let end: moment.Moment = moment()

      switch (view) {
        case 'day':
          start = moment(date).startOf('day')
          end = moment(date).endOf('day')
          break

        case 'week':
          start = moment(date).startOf('week')
          end = moment(date).endOf('week')
          break

        case 'month':
          start = moment(date)
            .startOf('month')
            .subtract(7, 'days')
          end = moment(date)
            .endOf('month')
            .add(7, 'days')
          break

        case 'agenda':
          start = moment(date).startOf('day')
          end = moment(date)
            .endOf('day')
            .add(1, 'month')
          break
      }

      this.props.onDateRangeChanged(start.unix(), end.unix())
    }
  }

  private _onNavigate(date: Date, view: string) {
    this.setState({
      currentDate: date,
    })
    this._handleDateRangeChange(date, view)
  }

  private _onSelectEvent(event: object) {
    const calEvent = event as ICalendarEvent

    switch (calEvent.type) {
      case CalendarEventType.Assignment:
        this.props.history.push(`/assignment/${calEvent.id}`)
        break

      case CalendarEventType.BrokeragePipeline:
        this.props.history.push(`/brokerage/pipeline/${calEvent.id}`)
        break

      case CalendarEventType.DueDiligence:
        this.props.history.push(`/development/sites/${calEvent.id}/pipeline`)
        break

      case CalendarEventType.Listing:
        this.props.history.push(`/brokerage/listing/${calEvent.id}`)
        break
    }
  }

  private _onView(view: string) {
    this.setState({
      currentView: view,
    })
    this._handleDateRangeChange(this.state.currentDate, view)
  }
}
